import {useCallback, useEffect, useRef, useState} from "react";
import {parseError} from "@/app/utils/error";
import {NearbyUsersModel, UserRequests} from "@/app/api/user";
import {useNavigate} from "react-router-dom";
import {route} from "@/app/routes/AppRoutes";
import useWatchUrlParam from "@/app/utils/use_watch_url_param";
import {SearchParamsKey, useUrlParams} from "@/app/utils/use_search_params";
import Cookies from "js-cookie";
import {CookiesKey} from "@/app/utils/cookies";
import {ChatModel, ChatRequests} from "@/app/api/chat";
import { logEventSafe } from "@/app/utils/analytics";

const PAGE_SIZE = 10;

interface ScreenState {
  hasError?: string;
  hasLoading: boolean;
  loadingMore: boolean;
  page: number
  isAll: boolean
  selectSeveral: boolean
  users?: NearbyUsersModel[]
  selectedUsers?: number[]
  selectedUser?: NearbyUsersModel
  chatLoadingUsers: number[]
}

const useNearbyPlayers = () => {
  const navigate = useNavigate();
  const {getUrlParam} = useUrlParams();

  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    hasLoading: true,
    loadingMore: false,
    page: 0,
    isAll: false,
    selectSeveral: false,
    users: [],
    selectedUsers: [],
    selectedUser: undefined,
    chatLoadingUsers: []
  });

  const updateState = useCallback((updates: Partial<ScreenState> | ((prev: ScreenState) => ScreenState)) => {
    setState((prev) => typeof updates === 'function' ? updates(prev) : ({...prev, ...updates}));
  }, []);

  useEffect(() => {
    // if (state.selectedUser === undefined) return;
  }, [state.selectedUser]);

  const hasLoadedInitial = useRef(false);
  const nextPageRef = useRef(0);
  const loadingRef = useRef(false);

  const init = useCallback(async () => {
    if (hasLoadedInitial.current) return;
    hasLoadedInitial.current = true;
    if (state.selectedUser === undefined) {
      const userId = getUrlParam(SearchParamsKey.userId);
      if (userId !== undefined) {
        navigate(route.findPlayers);
      }
    }

    if (loadingRef.current) return;
    loadingRef.current = true;
    updateState({hasLoading: true, hasError: undefined});
    try {
      const pageToLoad = nextPageRef.current;
      const users = await UserRequests.getUsersNearby(pageToLoad);
      updateState(prev => {
        const isAll = users.length < PAGE_SIZE;
        nextPageRef.current = isAll ? nextPageRef.current : nextPageRef.current + 1;
        return {
          ...prev,
          hasLoading: false,
          page: nextPageRef.current,
          users,
          isAll
        };
      });
      // Log players search event (no params currently)
      await logEventSafe('padel_search_players');
    } catch (e) {
      updateState({hasLoading: false, hasError: parseError(e).error});
    } finally {
      loadingRef.current = false;
    }
  }, [getUrlParam, navigate, updateState, state.selectedUser]);

  const loadMore = useCallback(async () => {
    if (state.isAll || state.loadingMore || state.hasLoading || loadingRef.current) return;
    try {
      loadingRef.current = true;
      updateState({loadingMore: true});
      const pageToLoad = nextPageRef.current;
      const users = await UserRequests.getUsersNearby(pageToLoad);
      updateState(prev => {
        const isAll = users.length < PAGE_SIZE;
        nextPageRef.current = isAll ? nextPageRef.current : nextPageRef.current + 1;
        return {
          ...prev,
          loadingMore: false,
          hasLoading: false,
          page: isAll ? prev.page : prev.page + 1,
          users: [...prev.users ?? [], ...users],
          isAll
        };
      });
    } catch (e) {
      updateState({hasLoading: false, loadingMore: false, hasError: parseError(e).error});
    } finally {
      loadingRef.current = false;
    }
  }, [state.hasLoading, state.isAll, state.loadingMore, updateState]);

  const changeSelectSeveral = useCallback(() => {
    if (state.selectSeveral) {
      updateState({selectedUsers: []})
    }
    updateState({selectSeveral: !state.selectSeveral});
  }, [updateState, state.selectSeveral]);

  const toggleUser = useCallback((id: number) => {
    if (state.selectedUsers?.includes(id)) {
      updateState({selectedUsers: state.selectedUsers?.filter((userId) => userId !== id)});
    } else {
      updateState({selectedUsers: [...state.selectedUsers ?? [], id]});
    }
  }, [updateState, state.selectedUsers]);

  useWatchUrlParam(SearchParamsKey.userId, (newValue) => {
    if (state.users?.length === 0) return;
    if (newValue) {
      updateState({selectedUser: state.users?.find((user) => user.id === Number(newValue))});
    } else {
      updateState({selectedUser: state.users?.find((user) => user.id === Number(newValue))});
    }
  });

  const openUserDetails = useCallback((user: NearbyUsersModel) => {
    navigate(`${route.detailPlayer}?userId=${user.id}&userName=${user.username}`);
  }, [updateState]);

  const createChat = useCallback(async (userId: number) => {
    updateState({chatLoadingUsers: [...state.chatLoadingUsers, userId]});
    try {
      const user = await UserRequests.getCurrentUser();
      const chat = await ChatRequests.createChat([user.user?.id, userId]) as ChatModel;
      navigate(route.chats + "?" + (SearchParamsKey.chat + "=" + chat.id));
    } catch (e) {
      console.log(e)
    }
    updateState({chatLoadingUsers: state.chatLoadingUsers.filter(id => id !== userId)});
  }, [updateState, navigate, state.chatLoadingUsers]);

  const goToCreateAMatch = useCallback((userIds: number[] | undefined) => {
    if (userIds === undefined) return;
    const users = state.users?.filter((user) => userIds.includes(user.id)) ?? [];
    Cookies.set(CookiesKey.users, JSON.stringify(users));
    navigate(route.createAMatch);
  }, [updateState, state.users]);

  const goToCreateAMatchSingle = useCallback((user: NearbyUsersModel) => {
    const users = [user];
    Cookies.set(CookiesKey.users, JSON.stringify(users));
    navigate(route.createAMatch);
  }, [navigate]);

  return {
    state,
    init,
    loadMore,
    changeSelectSeveral,
    toggleUser,
    openUserDetails,
    goToCreateAMatch,
    createChat,
    goToCreateAMatchSingle
  };
};

export default useNearbyPlayers;